(function () {
  'use strict';

  var REDUCED_MOTION  = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var TOUCH_DEVICE    = window.matchMedia('(pointer: coarse)').matches;
  var DESKTOP_POINTER = window.matchMedia('(pointer: fine)').matches;

  function lerp(a, b, t) { return a + (b - a) * t; }

  function zykusThrottle(fn, delay) {
    var last = 0;
    return function () {
      var now = Date.now();
      if (now - last >= delay) { last = now; fn.apply(this, arguments); }
    };
  }

  /* ── CURSOR ──────────────────────────────────────────────────── */
  function initCursor() {
    if (TOUCH_DEVICE) return;

    var dot  = document.createElement('div');
    var ring = document.createElement('div');
    dot.className  = 'zykus-cursor zykus-cursor--hidden';
    ring.className = 'zykus-cursor-ring zykus-cursor-ring--hidden';
    document.body.appendChild(dot);
    document.body.appendChild(ring);

    var mouseX = 0, mouseY = 0;
    var ringX  = 0, ringY  = 0;
    var cursorVisible = false;
    var rafId = null;

    function animateCursor() {
      ringX = lerp(ringX, mouseX, 0.12);
      ringY = lerp(ringY, mouseY, 0.12);
      ring.style.transform = 'translate3d(' + ringX + 'px,' + ringY + 'px,0)';
      rafId = requestAnimationFrame(animateCursor);
    }

    document.addEventListener('mousemove', function (e) {
      mouseX = e.clientX;
      mouseY = e.clientY;
      dot.style.transform = 'translate3d(' + e.clientX + 'px,' + e.clientY + 'px,0)';

      window.zykusCursor = { x: e.clientX, y: e.clientY };

      if (!cursorVisible) {
        cursorVisible = true;
        dot.classList.remove('zykus-cursor--hidden');
        ring.classList.remove('zykus-cursor-ring--hidden');
        if (!rafId) animateCursor();
      }
    });

    var HOVER_SEL = 'a, button, .card-wrapper, .button, [role="button"], label';

    document.body.addEventListener('mouseover', function (e) {
      if (e.target.closest(HOVER_SEL)) {
        dot.classList.add('zykus-cursor--hover');
        ring.classList.add('zykus-cursor-ring--hover');
      }
    });

    document.body.addEventListener('mouseout', function (e) {
      if (e.target.closest(HOVER_SEL)) {
        dot.classList.remove('zykus-cursor--hover');
        ring.classList.remove('zykus-cursor-ring--hover');
      }
    });

    document.addEventListener('mouseleave', function () {
      dot.classList.add('zykus-cursor--hidden');
      ring.classList.add('zykus-cursor-ring--hidden');
    });

    document.addEventListener('mouseenter', function () {
      if (cursorVisible) {
        dot.classList.remove('zykus-cursor--hidden');
        ring.classList.remove('zykus-cursor-ring--hidden');
      }
    });
  }

  /* ── SCROLL PROGRESS ─────────────────────────────────────────── */
  function initScrollProgress() {
    function update() {
      var scrollTop  = window.scrollY || document.documentElement.scrollTop;
      var docHeight  = document.documentElement.scrollHeight - window.innerHeight;
      var progress   = docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0;
      document.documentElement.style.setProperty('--scroll-progress', progress.toFixed(4));
    }
    window.addEventListener('scroll', zykusThrottle(update, 50), { passive: true });
    update();
  }

  /* ── 3D CARD TILT ────────────────────────────────────────────── */
  function initCardTilt() {
    if (REDUCED_MOTION || !DESKTOP_POINTER) return;

    var cards = document.querySelectorAll('.card-wrapper');

    cards.forEach(function (card) {
      card.addEventListener('mouseenter', function () {
        card.style.willChange = 'transform';
      });

      card.addEventListener('mousemove', function (e) {
        var rect = card.getBoundingClientRect();
        var nx = (e.clientX - rect.left  - rect.width  / 2) / (rect.width  / 2);
        var ny = (e.clientY - rect.top   - rect.height / 2) / (rect.height / 2);
        card.style.transform  = 'perspective(800px) rotateX(' + (-ny * 6) + 'deg) rotateY(' + (nx * 6) + 'deg) translateZ(8px)';
        card.style.boxShadow  = '0 20px 60px rgba(0,0,0,0.4)';
      });

      card.addEventListener('mouseleave', function () {
        card.style.transform = '';
        card.style.boxShadow = '';
        setTimeout(function () {
          if (!card.matches(':hover')) card.style.willChange = 'auto';
        }, 650);
      });
    });
  }

  /* ── WORD SPLIT REVEAL ───────────────────────────────────────── */
  function initWordReveal() {
    if (REDUCED_MOTION) return;

    var TARGETS = [
      '.image-with-text__heading',
      '.featured-collection .title',
      '.rich-text__heading'
    ];

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('zykus-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -80px 0px', threshold: 0.2 });

    TARGETS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        if (el.dataset.zykusSplit) return;
        if (el.children.length > 0) return;

        var text  = el.textContent.trim();
        if (!text) return;

        var words = text.split(/\s+/);
        el.innerHTML = words.map(function (word, i) {
          var escaped = word.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          return '<span class="zykus-word"><span class="zykus-char" style="--char-index:' + i + '">' + escaped + '</span></span>';
        }).join(' ');

        el.dataset.zykusSplit = '1';
        observer.observe(el);
      });
    });
  }

  /* ── SECTION LINES ───────────────────────────────────────────── */
  function initSectionLines() {
    if (REDUCED_MOTION) return;

    var LINE_SEL = '.featured-collection .title, .collection__title .title';

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('zykus-line-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -60px 0px', threshold: 0.1 });

    document.querySelectorAll(LINE_SEL).forEach(function (heading) {
      var next = heading.nextElementSibling;
      if (next && next.classList.contains('zykus-section-line')) return;

      var line = document.createElement('span');
      line.className = 'zykus-section-line';
      heading.parentNode.insertBefore(line, heading.nextSibling);
      observer.observe(line);
    });
  }

  /* ── HERO PARALLAX ───────────────────────────────────────────── */
  function initHeroParallax() {
    if (REDUCED_MOTION || TOUCH_DEVICE) return;

    var hero    = document.getElementById('zykus-hero');
    var content = document.getElementById('zykus-hero-content');
    if (!hero || !content) return;

    var heroTargetX = 0, heroTargetY = 0;
    var heroCurrentX = 0, heroCurrentY = 0;
    var MOVE_RANGE = 12;

    hero.addEventListener('mousemove', function (e) {
      var rect = hero.getBoundingClientRect();
      var nx = (e.clientX - rect.left  - rect.width  / 2) / (rect.width  / 2);
      var ny = (e.clientY - rect.top   - rect.height / 2) / (rect.height / 2);
      heroTargetX = nx * MOVE_RANGE;
      heroTargetY = ny * MOVE_RANGE;
    });

    hero.addEventListener('mouseleave', function () {
      heroTargetX = 0;
      heroTargetY = 0;
    });

    function animateHero() {
      heroCurrentX = lerp(heroCurrentX, heroTargetX, 0.06);
      heroCurrentY = lerp(heroCurrentY, heroTargetY, 0.06);
      content.style.transform = 'translate3d(' + heroCurrentX + 'px,' + heroCurrentY + 'px,0)';
      requestAnimationFrame(animateHero);
    }
    animateHero();
  }

  /* ── BOOT ────────────────────────────────────────────────────── */
  initCursor();
  initScrollProgress();

  if (!REDUCED_MOTION) {
    initCardTilt();
    initWordReveal();
    initSectionLines();
    initHeroParallax();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      initCardTilt();
      initWordReveal();
      initSectionLines();
    });
  }

})();
